/**
 * Helpers for the per-member preferred grocery / shopping retailer
 * (`member_profiles.preferred_store`). Lets the Grocery / Shopping
 * "send to store" CTAs adapt to the user's actual preference.
 *
 * Canonical enum values come from iOS `RetailerStore.swift:11-15`:
 * `[amazon, walmart, kroger, instacart, target]`. Web previously
 * persisted title-case values like "Target" / "Whole Foods" which
 * iOS+Android did NOT recognize. This file is now the source of
 * truth for read-back compatibility (accepts old + new values) and
 * write canonical-only.
 */

export type PreferredStoreId =
  | "amazon"
  | "walmart"
  | "kroger"
  | "instacart"
  | "target";

interface CanonicalStore {
  id: PreferredStoreId;
  label: string;
  search: (q: string) => string;
}

const CANONICAL_STORES: CanonicalStore[] = [
  {
    id: "amazon",
    label: "Amazon",
    search: (q) => `https://www.amazon.com/s?k=${encodeURIComponent(q)}`,
  },
  {
    id: "walmart",
    label: "Walmart",
    search: (q) => `https://www.walmart.com/search?q=${encodeURIComponent(q)}`,
  },
  {
    id: "kroger",
    label: "Kroger",
    search: (q) => `https://www.kroger.com/search?query=${encodeURIComponent(q)}`,
  },
  {
    id: "instacart",
    label: "Instacart",
    search: (q) =>
      `https://www.instacart.com/store/search/${encodeURIComponent(q)}`,
  },
  {
    id: "target",
    label: "Target",
    search: (q) =>
      `https://www.target.com/s?searchTerm=${encodeURIComponent(q)}`,
  },
];

/**
 * Legacy → canonical mapping. Web previously stored title-case strings
 * like "Target", "Walmart", and stores not in iOS's set ("Costco",
 * "Trader Joe's", etc.) are mapped to the closest canonical value or
 * dropped (returned as null).
 */
function legacyToCanonical(value: string | undefined | null): PreferredStoreId | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();

  // Already canonical
  if (CANONICAL_STORES.some((s) => s.id === v)) return v as PreferredStoreId;

  // Title-case web mapping (PR #81's original list)
  switch (v) {
    case "amazon":
      return "amazon";
    case "walmart":
      return "walmart";
    case "kroger":
      return "kroger";
    case "instacart":
      return "instacart";
    case "target":
      return "target";
    // The other web-only stores (Costco, Trader Joe's, Whole Foods,
    // Safeway, Aldi, Publix, Other) have no iOS equivalent. Treat as
    // unset rather than corrupt the cross-platform field.
    default:
      return null;
  }
}

/**
 * Resolve any stored value (legacy or canonical) to a CanonicalStore
 * record, or null if it can't be mapped.
 */
function resolveStore(stored: string | undefined): CanonicalStore | null {
  const id = legacyToCanonical(stored);
  if (!id) return null;
  return CANONICAL_STORES.find((s) => s.id === id) ?? null;
}

/**
 * The full list of pickable stores in the action sheet, with the
 * user's preferred (if set) floating to the top.
 */
export interface StoreOption {
  /** Canonical Firestore enum value to write. */
  id: PreferredStoreId;
  /** Display label for the picker chip / link. */
  label: string;
  /** Per-retailer search URL builder. */
  search: (q: string) => string;
}

export function getAllStoreOptions(preferred?: string): StoreOption[] {
  const preferredId = legacyToCanonical(preferred);
  if (!preferredId) return CANONICAL_STORES.map(toOption);
  const top = CANONICAL_STORES.find((s) => s.id === preferredId)!;
  const tail = CANONICAL_STORES.filter((s) => s.id !== preferredId);
  return [toOption(top), ...tail.map(toOption)];
}

function toOption(c: CanonicalStore): StoreOption {
  return { id: c.id, label: c.label, search: c.search };
}

/**
 * The `preferred_store` value to mark as currently selected in the
 * Account picker. Returns null if the stored value isn't recognized
 * (legacy "Costco" / "Trader Joe's" etc.) so the picker doesn't
 * highlight a phantom selection.
 */
export function getPreferredStoreId(
  stored: string | undefined,
): PreferredStoreId | null {
  return legacyToCanonical(stored);
}

/**
 * Display label for a stored value, or null if unrecognized.
 */
export function getPreferredStoreLabel(
  stored: string | undefined,
): string | null {
  return resolveStore(stored)?.label ?? null;
}

/**
 * The list of pickable stores for the Account preferred-store picker.
 */
export function getCanonicalStores(): StoreOption[] {
  return CANONICAL_STORES.map(toOption);
}
